/**
 * configure_mcp — AI 自动配置 MCP 服务器的常驻工具
 *
 * 用户在聊天中告诉 AI MCP 配置信息时，AI 调用此工具将配置保存到 MCP Store。
 * 安全校验：URL 必须为 HTTPS，拒绝私有 IP；且启用前必须经用户二次确认，
 * 防止 Prompt Injection（角色卡/世界书/记忆/工具返回中的恶意文本）诱导 AI 静默添加
 * 攻击者的 MCP 服务器，进而经 call_mcp_tool 外泄数据。
 */

import { useMCPClient } from '../composables/useMCPClient'
import { useGlobalConfirmStore } from '../stores/global-confirm'
import { useMCPStore } from '../stores/mcp'

/** 私有 IP 范围正则（防止 AI 被 Prompt Injection 指向内网） */
const PRIVATE_IP_RE = /^https?:\/\/(?:localhost|127\.|10\.|192\.168\.|172\.(?:1[6-9]|2\d|3[01])\.)/i

export const configureMCPTool = {
  function: {
    name: 'configure_mcp',
    description: '当用户提供 MCP 服务器的配置信息时（包括名称、地址 URL、API Key 等），调用此工具将其保存到用户的工具列表中并立即启用。',
    parameters: {
      type: 'object' as const,
      properties: {
        name: {
          type: 'string',
          description: 'MCP 服务器的显示名称，如「瑞幸点单」',
        },
        url: {
          type: 'string',
          description: 'MCP 服务器的完整 HTTPS 地址',
        },
        description: {
          type: 'string',
          description: '该 MCP 服务器的用途描述（可选）',
        },
        apiKey: {
          type: 'string',
          description: '认证 Token / API Key（可选）',
        },
      },
      required: ['name', 'url'],
    },
  },
  execute: async (args: unknown): Promise<string> => {
    const { name, url, description, apiKey } = args as {
      name: string
      url: string
      description?: string
      apiKey?: string
    }

    // 安全校验：必须 HTTPS，拒绝私有/本地 IP
    if (!url.startsWith('https://')) {
      return '❌ 只支持 HTTPS 地址，已拒绝保存。'
    }
    if (PRIVATE_IP_RE.test(url)) {
      return '❌ 不允许配置私有 IP 或本地地址，已拒绝保存。'
    }

    const mcpStore = useMCPStore()

    // 查重：同 URL（规范化尾斜杠后比较）或同名视为已存在，不重复添加。
    // 场景：用户二次提供配置、AI 看不到已有服务器时会重复调用本工具（曾导致工具数翻倍）。
    const normalize = (u: string): string => u.trim().replace(/\/+$/, '').toLowerCase()
    const targetUrl = normalize(url)
    const targetName = name.trim().toLowerCase()
    const existing = mcpStore.servers.find(
      s => normalize(s.url) === targetUrl || s.name.trim().toLowerCase() === targetName,
    )
    if (existing) {
      return `ℹ️ MCP 服务器「${existing.name}」已存在（未重复添加）。如需查看或管理，可用 list_mcp_servers，或到 设置 → 机体模块 → 工具 (MCP)。`
    }

    // 二次确认：AI 添加并启用外部 MCP 服务器 = 把用户数据交给该服务器处理，
    // 必须由用户亲自确认名称与地址，杜绝 Prompt Injection 静默接入攻击者服务器。
    const confirmed = await useGlobalConfirmStore().confirm({
      title: '添加 MCP 服务器',
      message: `AI 请求添加并启用 MCP 服务器「${name.trim()}」：\n${url.trim()}\n\n启用后 AI 可调用该服务器的工具，你的相关数据会发送给它。确认添加？`,
      confirmText: '添加并启用',
      cancelText: '取消',
      confirmVariant: 'primary',
    })
    if (!confirmed) {
      return `用户取消了添加 MCP 服务器「${name.trim()}」，未做任何更改。`
    }

    const server = mcpStore.addServer({
      name: name.trim(),
      url: url.trim(),
      description: description?.trim(),
      apiKey: apiKey?.trim() || undefined,
      enabled: true,
    })

    // 立即拉取工具列表并缓存，让 AI 下一轮就能看到并调用这些工具——
    // 否则 cachedTools 为空，AI 会误以为没工具而 fallback 到 plugin_action。
    // 同步失败不影响"已添加"这个事实：服务器已存进 Store，用户仍可去设置页手动同步。
    try {
      const tools = await useMCPClient().listTools(server)
      mcpStore.updateCachedTools(server.id, tools)
      return `✅ 已成功添加 MCP 服务器「${name}」并启用，已同步 ${tools.length} 个工具，现在就能用了。可在 设置 → 机体模块 → 工具 (MCP) 中查看和管理。`
    }
    catch (error) {
      console.warn('[configure_mcp] 自动同步工具失败:', error)
      return `⚠️ 已添加并启用 MCP 服务器「${name}」，但自动同步工具列表失败（可能是地址或网络问题）。请到 设置 → 机体模块 → 工具 (MCP) 里点「同步工具」重试。`
    }
  },
}
